import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { playIntro, playEnding, startAmbience, stopAmbience, phoneEq, isPhone } from "@/lib/soundScene";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { PersonaGrid } from "@/components/roleplay/PersonaGrid";
import { Scorecard } from "@/components/roleplay/Scorecard";
import { LiveCall } from "@/components/roleplay/LiveCall";
export { CHANNEL_ICONS, ChannelPicker, LanguagePicker } from "@/components/roleplay/PersonaGrid";

export default function Roleplay() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const battleId = searchParams.get("battle");
  const tournamentId = searchParams.get("tournament");
  const matchId = searchParams.get("match");
  const modeParam = searchParams.get("mode");
  const battlePersona = searchParams.get("persona");
  const channelParam = searchParams.get("channel");
  const sessionParam = searchParams.get("session");
  const [personas, setPersonas] = useState([]);
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState(channelParam || user?.primary_channel || "door_to_door");
  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState(() => searchParams.get("lang") || localStorage.getItem("pc_lang") || "en");
  useEffect(() => { localStorage.setItem("pc_lang", language); }, [language]);
  const [product, setProduct] = useState(user?.product || "");
  const [persona, setPersona] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [xpGained, setXpGained] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [voiceOn, setVoiceOn] = useState(true);
  const [sceneOn, setSceneOn] = useState(() => localStorage.getItem("pc_scene") !== "off");
  const [customerLeft, setCustomerLeft] = useState(false);
  const toggleScene = () => {
    setSceneOn((on) => {
      const next = !on;
      localStorage.setItem("pc_scene", next ? "on" : "off");
      if (!next) stopAmbience();
      else if (session?.channel) startAmbience(session.channel);
      return next;
    });
  };
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [handsFree, setHandsFree] = useState(false);
  const [hfState, setHfState] = useState("idle"); // idle | listening | thinking | speaking
  const [speaking, setSpeaking] = useState(false);
  const [level, setLevel] = useState(0);
  const [customDials, setCustomDials] = useState(null);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const handsFreeRef = useRef(false);
  const hfStreamRef = useRef(null);
  const spokeRef = useRef(false);
  const bargedRef = useRef(false);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const meterCtxRef = useRef(null);
  const meterRafRef = useRef(null);

  const meterAudio = (a, channelKey) => {
    try {
      const ctx = meterCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      meterCtxRef.current = ctx;
      ctx.resume?.();
      const src = ctx.createMediaElementSource(a);
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      const tail = sceneOn && isPhone(channelKey) ? phoneEq(ctx, src) : src;
      tail.connect(an);
      an.connect(ctx.destination);
      const buf = new Uint8Array(an.frequencyBinCount);
      let frame = 0;
      const tick = () => {
        if (a.paused || a.ended) { setLevel(0); return; }
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        if (frame++ % 2 === 0) setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3.5));
        meterRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) { console.warn("[Roleplay] ignored error", err); }
  };

  const [replaying, setReplaying] = useState(null);
  const replay = async (i, text) => {
    if (replaying === i) { audioRef.current?.pause(); setSpeaking(false); setReplaying(null); return; }
    setReplaying(i);
    try { await playTTS(text); } finally { setReplaying(null); }
  };

  const ttsSeqRef = useRef(0);
  const cleanSpoken = (text) => text
    .replace(/\*([^*]+)\*/g, (_, inner) => (inner.trim().split(/\s+/).length >= 3 ? " " : inner))
    .replace(/\([^)]*\)/g, " ").replace(/[_#`>"]+/g, "").replace(/\s+/g, " ").trim();
  const fetchTTS = async (text, voice, speed, language) => {
    const spoken = cleanSpoken(text);
    if (!spoken) return null;
    try {
      const { data } = await api.post("/voice/tts", { text: spoken, voice: voice || session?.voice, speed: speed || session?.voice_speed || 1.0, language: language || session?.language || "en" });
      return data.audio_base64 || null;
    } catch { return null; }
  };
  const playAudioB64 = (b64, channelOverride, seq) => new Promise((resolve) => {
    if (!b64 || (seq != null && seq !== ttsSeqRef.current)) return resolve();
    if (audioRef.current) audioRef.current.pause();
    if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
    const a = new Audio(`data:audio/mp3;base64,${b64}`);
    audioRef.current = a;
    const done = () => { setSpeaking(false); setLevel(0); resolve(); };
    a.onended = done;
    a.onerror = done;
    setSpeaking(true);
    meterAudio(a, channelOverride || session?.channel);
    a.play().catch(done);
  });
  const pendingAudioRef = useRef(null);
  const playTTS = async (text, voiceOverride, channelOverride, speedOverride) => {
    const seq = ++ttsSeqRef.current;
    const pre = pendingAudioRef.current;
    const b64 = pre && pre.text === text ? (pendingAudioRef.current = null, pre.b64) : await fetchTTS(text, voiceOverride, speedOverride);
    await playAudioB64(b64, channelOverride, seq);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await sendAudio(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setVoiceOn(true);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const stopRec = () => { if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); } };

  const sendAudio = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "speech.webm");
      if (session?.language) fd.append("language", session.language);
      const { data } = await api.post("/voice/transcribe", fd);
      const text = (data.text || "").trim();
      if (!text) { toast.error("Didn't catch that — try again."); return; }
      await send(text, true);
    } catch {
      toast.error("Couldn't transcribe your voice.");
    } finally {
      setTranscribing(false);
    }
  };

  // ---- Hands-free continuous conversation ----
  const beginHandsFree = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      hfStreamRef.current = stream;
      handsFreeRef.current = true;
      setHandsFree(true);
      setVoiceOn(true);
      listenTurn();
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const endHandsFree = () => {
    handsFreeRef.current = false;
    setHandsFree(false);
    setHfState("idle");
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { mediaRef.current?.state === "recording" && mediaRef.current.stop(); } catch (err) { console.warn("[Roleplay] ignored error", err); }
    hfStreamRef.current?.getTracks().forEach((t) => t.stop());
    hfStreamRef.current = null;
    try { audioCtxRef.current?.close(); } catch (err) { console.warn("[Roleplay] ignored error", err); }
    audioCtxRef.current = null;
  };

  const listenTurn = () => {
    if (!handsFreeRef.current || !hfStreamRef.current) return;
    const stream = hfStreamRef.current;
    const mr = new MediaRecorder(stream);
    const chunks = [];
    spokeRef.current = false;
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = async () => {
      if (!handsFreeRef.current) return;
      if (!spokeRef.current) { listenTurn(); return; }
      const barged = bargedRef.current; bargedRef.current = false;
      setHfState("thinking");
      if (barged) toast(`${persona.name} cut you off!`, { icon: "✋" });
      const blob = new Blob(chunks, { type: "audio/webm" });
      try {
        const fd = new FormData();
        fd.append("audio", blob, "speech.webm");
        if (session?.language) fd.append("language", session.language);
        const { data } = await api.post("/voice/transcribe", fd);
        const text = (data.text || "").trim();
        if (!text) { if (handsFreeRef.current) { setHfState("listening"); listenTurn(); } return; }
        const reply = await send(text, true, barged);
        if (reply) { setHfState("speaking"); await playTTS(reply); }
      } catch (err) { console.warn("[Roleplay] ignored error", err); }
      if (handsFreeRef.current) { setHfState("listening"); listenTurn(); }
    };
    mr.start();
    mediaRef.current = mr;
    setHfState("listening");
    monitorSilence(stream, mr);
  };

  const monitorSilence = (stream, mr) => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    src.connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const start = Date.now();
    let silenceStart = null;
    const THRESH = 12, SILENCE_MS = 1300, MAX_MS = 20000, MIN_SPEAK_MS = 400;
    let speakStart = null;
    let bargeDeadline = null;
    const barge = persona?.barge_in || 0;
    const tick = () => {
      if (!handsFreeRef.current || mr.state !== "recording") return;
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = buf[i] - 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      const now = Date.now();
      if (rms > THRESH) {
        if (!speakStart) speakStart = now;
        if (now - speakStart > MIN_SPEAK_MS) {
          spokeRef.current = true;
          if (barge && !bargeDeadline) bargeDeadline = now + (2200 + (1 - barge) * 3500 + Math.random() * 1400);
        }
        silenceStart = null;
      } else if (spokeRef.current) {
        if (!silenceStart) silenceStart = now;
        else if (now - silenceStart > SILENCE_MS) { try { mr.stop(); } catch (err) { console.warn("[Roleplay] ignored error", err); }; try { ctx.close(); } catch (err) { console.warn("[Roleplay] ignored error", err); }; return; }
      }
      // barge-in: an impatient customer cuts the salesperson off mid-sentence
      if (barge && bargeDeadline && spokeRef.current && now > bargeDeadline) {
        bargedRef.current = true;
        try { mr.stop(); } catch (err) { console.warn("[Roleplay] ignored error", err); }; try { ctx.close(); } catch (err) { console.warn("[Roleplay] ignored error", err); }; return;
      }
      if (now - start > MAX_MS) { try { mr.stop(); } catch (err) { console.warn("[Roleplay] ignored error", err); }; try { ctx.close(); } catch (err) { console.warn("[Roleplay] ignored error", err); }; return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => () => {
    endHandsFree();
    stopAmbience();
    audioRef.current?.pause();
    if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
    try { meterCtxRef.current?.close(); } catch (err) { console.warn("[Roleplay] ignored error", err); }
  }, []); // cleanup on unmount

  useEffect(() => {
    api.get("/personas").then((r) => setPersonas(r.data)).catch((err) => console.warn("[Roleplay] request failed", err));
    api.get("/channels").then((r) => setChannels(r.data)).catch((err) => console.warn("[Roleplay] request failed", err));
    api.get("/languages").then((r) => setLanguages(r.data)).catch((err) => console.warn("[Roleplay] request failed", err));
  }, []);
  useEffect(() => { if (!channelParam && user?.primary_channel) setChannel(user.primary_channel); }, [user?.primary_channel]); // eslint-disable-line
  useEffect(() => { if (user?.product && !product) setProduct(user.product); }, [user?.product]); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  // Auto-start when launched from a battle, tournament, or drill mode
  useEffect(() => {
    if (personas.length && !session && !starting) {
      if (battlePersona) {
        const p = personas.find((x) => x.id === battlePersona);
        if (p) start(p);
      } else if (modeParam === "comeback") {
        const p = personas.find((x) => x.id === "interrupter");
        if (p) start({ ...p, title: "Comeback Drill" });
      }
    }
  }, [battlePersona, modeParam, personas]); // eslint-disable-line

  // Resume a saved conversation (deep link / refresh / "pick up where you left off")
  useEffect(() => {
    if (sessionParam && personas.length && !session && !starting && !analysis) resume(sessionParam);
  }, [sessionParam, personas]); // eslint-disable-line

  const resume = async (sid) => {
    setStarting(true);
    try {
      const { data } = await api.get(`/roleplay/${sid}`);
      const base = personas.find((x) => x.id === data.persona_id) || { id: data.persona_id, name: data.persona_name, title: data.persona_title, avatar: data.persona_avatar };
      const hasDials = data.overrides && Object.keys(data.overrides).length > 0;
      setPersona({ ...base, ...(data.overrides || {}), title: data.persona_title || base.title });
      setCustomDials(hasDials ? data.overrides : null);
      setSession(data);
      setMessages(data.messages || []);
      setCustomerLeft(!!data.ended_by_customer);
      setSearchParams((p) => { p.set("session", data.id); return p; }, { replace: true });
      if (data.status !== "active" && data.analysis) { setAnalysis(data.analysis); return; }
      if (data.status !== "active") { toast.error("That conversation has already ended."); reset(); return; }
      if (sceneOn) startAmbience(data.channel);
      toast.success(`Back with ${data.persona_name}`, { description: "Picked up right where you left off." });
    } catch (e) {
      toast.error("Couldn't find that conversation.");
      setSearchParams((p) => { p.delete("session"); return p; }, { replace: true });
    } finally {
      setStarting(false);
    }
  };

  const start = async (p, dials = null) => {
    setStarting(true);
    setPersona(p);
    setCustomDials(dials);
    setCustomerLeft(false);
    // Play the scene intro (doorbell / ring) immediately so it masks the AI's thinking time.
    const introP = sceneOn && channel ? (startAmbience(channel), playIntro(channel)) : Promise.resolve();
    try {
      const { data } = await api.post("/roleplay/start", { persona_id: p.id, with_audio: voiceOn, battle_id: battleId || undefined, tournament_id: tournamentId || undefined, match_id: matchId || undefined, mode: modeParam || undefined, overrides: dials || undefined, channel, product: product.trim() || undefined, language });
      setSession(data);
      setMessages(data.messages);
      setAnalysis(null);
      setStarting(false);
      setSearchParams((sp) => { sp.set("session", data.id); return sp; }, { replace: true });
      if (sceneOn && !channel) startAmbience(data.channel);
      const opener = data.messages?.[0];
      const seq = ++ttsSeqRef.current;
      // Opening audio comes inline with the start response; fall back to a fetch if it didn't.
      const audioP = voiceOn && opener?.role === "assistant" ? (data.audio_base64 ? Promise.resolve(data.audio_base64) : fetchTTS(opener.content, data.voice, data.voice_speed, data.language)) : Promise.resolve(null);
      const [b64] = await Promise.all([audioP, introP]);
      playAudioB64(b64, data.channel, seq);
    } catch (e) {
      toast.error("Couldn't start the roleplay. Try again.");
      setPersona(null);
      stopAmbience();
    } finally {
      setStarting(false);
    }
  };

  const send = async (textArg, spoken = false, interrupted = false) => {
    const text = (typeof textArg === "string" ? textArg : input).trim();
    if (!text || sending) return null;
    if (typeof textArg !== "string") setInput("");
    setMessages((m) => [...m, { role: "user", content: text, after_barge: interrupted }]);
    setSending(true);
    try {
      const { data } = await api.post(`/roleplay/${session.id}/message`, { text, interrupted, with_audio: voiceOn || spoken, speed: session?.voice_speed || undefined });
      if (data.audio_base64) pendingAudioRef.current = { text: data.reply, b64: data.audio_base64 };
      setMessages((m) => [...m, { role: "assistant", speaker: data.speaker, content: data.reply }]);
      if (data.ended_by_customer) {
        setCustomerLeft(true);
        endHandsFree();
        const finish = async () => {
          if (sceneOn) { stopAmbience(); await playEnding(session.channel); }
          toast(`${persona?.name || "The customer"} ended the conversation`, { description: "Hit End & Analyse to see what went wrong — and how to recover next time.", icon: "🚪" });
        };
        if ((voiceOn || spoken) && !handsFreeRef.current) playTTS(data.reply).then(finish); else finish();
        return null;
      }
      if ((voiceOn || spoken) && !handsFreeRef.current) playTTS(data.reply);
      return data.reply;
    } catch (e) {
      toast.error("The customer didn't respond. Try again.");
      return null;
    } finally {
      setSending(false);
    }
  };

  const end = async () => {
    endHandsFree();
    if (messages.filter((m) => m.role === "user").length === 0) {
      toast.error("Say something first — you can't analyse an empty conversation.");
      return;
    }
    setEnding(true);
    stopAmbience();
    audioRef.current?.pause();
    try {
      const { data } = await api.post(`/roleplay/${session.id}/end`);
      setAnalysis(data.analysis);
      setXpGained(data.xp_gained);
      setAssessment(data.assessment || null);
      refreshUser();
      toast.success(`Analysis ready · +${data.xp_gained} XP`);
      if (data.assessment?.certificate) toast.success("Assessment passed — certificate earned!", { description: `${data.assessment.course_title} · view it in Courses.` });
    } catch (e) {
      toast.error("Couldn't analyse. Try again.");
    } finally {
      setEnding(false);
    }
  };

  const reset = () => {
    ttsSeqRef.current++;
    endHandsFree();
    stopAmbience();
    audioRef.current?.pause();
    setPersona(null); setSession(null); setMessages([]); setAnalysis(null); setXpGained(null); setCustomerLeft(false); setAssessment(null);
    if (battleId) { setSearchParams({}); navigate("/app/battles"); }
    else if (tournamentId) { setSearchParams({}); navigate("/app/tournaments"); }
    else if (modeParam) { setSearchParams({}); navigate("/app/challenges"); }
    else setSearchParams((sp) => { sp.delete("session"); return sp; }, { replace: true });
  };

  if (analysis) return <Scorecard analysis={analysis} persona={persona} xpGained={xpGained} sessionId={session?.id} assessment={assessment} onRetry={() => start(persona, customDials)} onBack={reset} />;

  if (!session) {
    if (starting || (sessionParam && !analysis)) return <div className="flex items-center justify-center py-32 text-emerald-600" data-testid="roleplay-loading"><Loader2 className="w-6 h-6 animate-spin mr-2" /> {sessionParam ? "Picking up your conversation…" : "Setting the scene…"}</div>;
    return <PersonaGrid personas={personas} onSelect={start} onResume={resume} starting={starting} channels={channels} channel={channel} setChannel={setChannel} primaryChannel={user?.primary_channel} product={product} setProduct={setProduct} languages={languages} language={language} setLanguage={setLanguage} />;
  }
  return (
    <LiveCall persona={persona} session={session} messages={messages} input={input} setInput={setInput} sending={sending} ending={ending}
      transcribing={transcribing} recording={recording} speaking={speaking} level={level} handsFree={handsFree} hfState={hfState}
      voiceOn={voiceOn} setVoiceOn={setVoiceOn} sceneOn={sceneOn} toggleScene={toggleScene} customDials={customDials} customerLeft={customerLeft}
      replaying={replaying} scrollRef={scrollRef} onExit={reset} onRetry={start} onSend={send} onEnd={end} onStartRec={startRec} onStopRec={stopRec}
      onBeginHandsFree={beginHandsFree} onEndHandsFree={endHandsFree} onReplay={replay} />
  );
}
